import bindAllMethods from '../../../utils/bindAllMethods';
import AuthContextEnum from '../../authTokenService/AuthContextEnum';
import type IRefreshTokenService from './IRefreshTokenService';
import { RefreshParameterType } from '../ISessionService';
import { Lifecycle, inject, injectable, scoped } from 'tsyringe';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { TenantStrategyEnum } from '../../tenantHandler/strategy/TenantStrategyEnum';
import IExchangeTokenService from '../exchangeTokenService/IExchangeTokenService';
import ExchangeTokenServiceNative from '../exchangeTokenService/ExchangeTokenServiceNative';
import * as JWebTypes from '../../JWeb/types';
import { TokenLifetimeRequirementType, TokenType } from '../../JWeb/JWebEnums';
import { AuthTokenService, IAuthTokenService } from '../../authTokenService';
import IApplicationService from '../../../services/applicationService/IApplicationService';
import { ApplicationContextEnum } from '../../../services/applicationService/enums';
import type {
  AuthProvidersPropsType,
  ShellFlagsInputType
} from '../../../infra/commonInitializer/types';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import getConfigIdBasedOnStack from '../utils/getConfigIdBasedOnStack';
import { AuthPluginFacade } from '../../../services/JWeb';

@injectable()
@scoped(Lifecycle.ContainerScoped)
class RefreshTokenServiceNative implements IRefreshTokenService {
  private _refreshPromise: Promise<void>;
  private _authTokenService: IAuthTokenService;
  private _exchangeTokenServiceNative: IExchangeTokenService;
  private _applicationService: IApplicationService;
  private _authProviderProps: AuthProvidersPropsType;
  private _forceDefaultAdditionalAuthorizationParams: boolean;

  constructor(
    @inject(AuthTokenService)
    authTokenService: IAuthTokenService,
    @inject(ExchangeTokenServiceNative)
    exchangeTokenServiceNative: IExchangeTokenService,
    @inject('ApplicationService') applicationService: IApplicationService,
    @inject('AuthProviderProps')
    authProviderProps: AuthProvidersPropsType,
    @inject('ShellFlagsProps') shellFlagsProps: ShellFlagsInputType
  ) {
    this._authTokenService = authTokenService;
    this._exchangeTokenServiceNative = exchangeTokenServiceNative;
    this._applicationService = applicationService;
    this._authProviderProps = authProviderProps;
    this._forceDefaultAdditionalAuthorizationParams =
      shellFlagsProps?.forceDefaultAdditionalAuthorizationParams;
    bindAllMethods(this);
  }

  private old_getAdditionalParamsForJwebGetToken() {
    internalLogger.log('Using default additionalAuthorizationParameters');

    return {
      additionalAuthorizationParameters: {
        config_id: getConfigIdBasedOnStack(
          this._applicationService.getPortalStack()
        )
      }
    };
  }

  private getAdditionalParamsForJwebGetToken() {
    // JWEB has a bug when we do not pass the additionalAuthorizationParameters. We created a workaround using shellFlag to pass a default parameter.
    if (this._forceDefaultAdditionalAuthorizationParams) {
      return this.old_getAdditionalParamsForJwebGetToken();
    }

    internalLogger.log('Using empty additionalAuthorizationParameters');
    return {};
  }

  public async refresh(
    refreshParameterType?: RefreshParameterType
  ): Promise<void> {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshAction = async () => {
      //TODO Fix why the setStratusAccessTokenFromNative is needed then remove this from here
      const authContext = await this._setStratusAccessTokenFromNative();

      // @TODO check if this is needed to pass for the exchange
      const tenantId = this._authTokenService.getTenantIdFromToken(authContext);

      if (!this._checkIfWebTokenIsRequired()) {
        this._exchangeNativeTokenIntoWebToken(tenantId, authContext);
      }
    };

    this._refreshPromise = refreshAction().finally(() => {
      this._refreshPromise = undefined;
    });

    return this._refreshPromise;
  }

  private _checkIfWebTokenIsRequired(): boolean {
    const isNativeAppStratus =
      this._authProviderProps.initialAuthenticationProvider ===
      AuthenticationProviderEnum.authz;
    const isManifestStratus =
      this._authProviderProps.authenticationProvider ===
      AuthenticationProviderEnum.authz;
    return (
      isNativeAppStratus &&
      isManifestStratus &&
      this._applicationService.getApplicationContext() ===
        ApplicationContextEnum.NATIVE_PORTAL
    );
  }

  private async _exchangeNativeTokenIntoWebToken(
    tenantId: string,
    authContext: AuthContextEnum
  ): Promise<void> {
    await this._exchangeTokenServiceNative.exchange({
      tenantId: tenantId || null,
      authContext,
      tenantStrategy: TenantStrategyEnum.stratusNative,
      jwebTokenOptions: {
        tokenType: TokenType.user,
        requireFreshToken: true
      }
    });
  }

  private async _setStratusAccessTokenFromNative(): Promise<AuthContextEnum> {
    try {
      const tokenProviderOptions = {
        tokenType: TokenType.user,
        tokenLifetimeRequirements: [
          {
            type: TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration,
            timeInterval: 60 * 10
          }
        ],
        ...this.getAdditionalParamsForJwebGetToken()
      } as JWebTypes.TokenProviderOptions;

      const { tokenValue } = (await AuthPluginFacade.getToken({
        tokenProviderOptions
      })) as JWebTypes.AccessToken;

      const haveAccessToken =
        typeof tokenValue === 'string' && tokenValue.length > 1;

      if (haveAccessToken) {
        const isTenantToken =
          !!this._authTokenService.getTenantIdFromGivenToken(tokenValue);

        const authContext = isTenantToken
          ? AuthContextEnum.tenant
          : AuthContextEnum.tenantless;

        this._authTokenService.setToken(tokenValue, authContext);

        if (isTenantToken) {
          const { tokenValue: tenantlessTokenValue } =
            this._applicationService.getAuthenticationProvider() ===
            AuthenticationProviderEnum.authz
              ? ((await AuthPluginFacade.getToken({
                  tokenProviderOptions: {
                    ...tokenProviderOptions,
                    tenantID: null
                  }
                })) as JWebTypes.AccessToken)
              : { tokenValue };

          this._authTokenService.setToken(
            tenantlessTokenValue,
            AuthContextEnum.tenantless
          );
        }

        return authContext;
      } else {
        this._authTokenService.deleteToken(AuthContextEnum.tenantless);
      }
    } catch (error) {
      this._authTokenService.deleteToken(AuthContextEnum.tenantless);
      internalLogger.error('Error on getToken');
      console.error(error);
      return;
    }
    return AuthContextEnum.tenantless;
  }
}

export default RefreshTokenServiceNative;
